// Password policy shared by every surface that SETS a password: the onboarding
// wizard, the forgot-password Change Password page, and the in-app Settings
// change-password modal.
//
// The AUTHORITATIVE rule lives server-side — the ArcadeBrain platform enforces it
// on every password write (PUT /web/user-profile, POST /web/change-password).
// These client-side values MUST mirror the server minimum so a too-short password
// is blocked BEFORE the request instead of the server rejecting it and the wizard
// swallowing that rejection (which silently locked new users out of the account
// they just created). If the server minimum changes, update PASSWORD_MIN_LENGTH —
// it is the single source of truth for the gate AND the requirement checklist.
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 50;

// eslint-disable-next-line
const SPECIAL_CHARS = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;

// The ONE ordered list of password rules. Both the validation gate
// (getPasswordPolicyError) and the live checklist (PasswordRequirements) derive
// from this array, so the gate and the UI can never disagree. Each rule carries
// the i18n key for its checklist row (reqKey) and its validation toast (toastKey).
const PASSWORD_RULES = [
  {
    reqKey: "auth.changePassword.req.length",
    toastKey: "wizard.password.toast.length",
    test: (pw) => pw.length >= PASSWORD_MIN_LENGTH && pw.length <= PASSWORD_MAX_LENGTH,
  },
  {
    // No lowercase letter present → the string is unchanged by toUpperCase().
    reqKey: "auth.changePassword.req.lowercase",
    toastKey: "wizard.password.toast.lowercase",
    test: (pw) => pw !== pw.toUpperCase(),
  },
  {
    // No uppercase letter present → the string is unchanged by toLowerCase().
    reqKey: "auth.changePassword.req.uppercase",
    toastKey: "wizard.password.toast.uppercase",
    test: (pw) => pw !== pw.toLowerCase(),
  },
  {
    reqKey: "auth.changePassword.req.number",
    toastKey: "wizard.password.toast.number",
    test: (pw) => /\d/.test(pw),
  },
  {
    reqKey: "auth.changePassword.req.special",
    toastKey: "wizard.password.toast.special",
    test: (pw) => SPECIAL_CHARS.test(pw),
  },
];

// Per-rule pass/fail for the live checklist, in display order: [{ reqKey, ok }].
// The single source shared with getPasswordPolicyError so the checklist and the
// submit gate can never drift apart.
export function getPasswordRuleChecks(password) {
  const pw = password || "";
  return PASSWORD_RULES.map((rule) => ({ reqKey: rule.reqKey, ok: rule.test(pw) }));
}

// Pure validator. Returns the i18n toast key of the FIRST unmet rule (same order
// as the checklist), or null when the password and its confirmation satisfy every
// rule. No side effects — the caller maps the key to a toast.
export function getPasswordPolicyError(password, confirmPassword) {
  const pw = password || "";
  for (const rule of PASSWORD_RULES) {
    if (!rule.test(pw)) {
      return rule.toastKey;
    }
  }
  if (pw !== confirmPassword) {
    return "wizard.password.toast.mismatch";
  }
  return null;
}

// True when a failed profile-save is the SERVER rejecting our payload, as opposed
// to a transient/offline failure (network error, 5xx, no response). This codebase
// has TWO rejection channels and both must be caught:
//   1. A non-2xx response — the api.js interceptor throws an Error whose `.cause`
//      is the axios error, so the HTTP status is at error.cause.response.status.
//   2. An HTTP-200 logical rejection — the caller throws a bare Error tagged with
//      `.isServerRejection = true` (the `{ ok:false, errors:[] }` convention).
// Either way the save was rejected, NOT dropped offline: the offline-retry path
// strips the password (so it can never re-set it) and only runs after a
// successful login — impossible if the password never persisted. A rejection must
// surface to the user and keep them on the wizard to fix it.
export function isServerRejection(error) {
  if (error?.isServerRejection === true) {
    return true;
  }
  const status = error?.cause?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
}

// Best-effort extraction of the server's human-readable reason. Prefers an
// explicit `serverDetail` set on a tagged ok:false rejection, then the axios body
// `detail` (ArcadeBrain's user-facing sentence, e.g. "Password must be at least 12
// characters ..."), then a generic `message`. Returns null so the caller can fall
// back to a localized message.
export function getServerErrorDetail(error) {
  if (typeof error?.serverDetail === "string" && error.serverDetail.trim()) {
    return error.serverDetail;
  }
  const data = error?.cause?.response?.data;
  if (data && typeof data === "object") {
    const candidate = data.detail || data.message;
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }
  }
  return null;
}
